import { columnTypes, dataTypes, Table } from 'cat-ecommerce-alloy';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styles from '../../../../../app.scss';
import checkoutStyles from '../../checkout-styles.scss';
import declarations from '../../PickUpAndDelivery/declarations.proptypes';
import ItemOrderTableAvalabilityLabel from './ItemOrderTableAvalablityLabel';
import ItemsInOrderTableCondensedRow from './ItemsInOrderTableCondensedRow';
import ItemsInOrderTableNotes from './ItemsInOrderTableNotes';
import ItemsInOrderTablePartDescription from './ItemsInOrderTablePartDescription';
import ItemsInOrderTablePrice from './ItemsInOrderTablePrice';
import ItemsInOrderTablePriceLabel from './ItemsInOrderTablePriceLabel';
import ItemsInOrderTableUnitPrice from './ItemsInOrderTableUnitPrice';
import ItemsInOrderTableImage from './ItemsInOrderTableImage';
import { useBreakpoint } from '@app/hooks';
import {
  ORDER_TYPES,
  PAGE_BREAKPOINTS,
  USER_TYPE_GUEST
} from '@app/constants/commonConstants';
import {
  TABLE_CELL_STYLES_DESKTOP,
  TABLE_CELL_STYLES_MOBILE
} from '@app/constants/tableConstants';
import { useSelector } from 'react-redux';
import stylesModule from '../../../summary-and-payment/styles.module.scss';
import { getUpdatedSchema } from './utils';
import Availability from '@app/components/common/Availability';
import { useLocation } from 'react-router';
import queryString from 'query-string';

const ItemsInOrderTable = ({
  currencyNotation,
  displayAvailabityAndSourcingDetails,
  includeGST,
  taxDisclaimerMessage,
  isQuantityMergeEnabled,
  items = [],
  listName,
  showAvailabilityColumn = true,
  showGST,
  showGstDagger,
  showUnitPriceColumn = false,
  orderItemPricesDetails,
  isPendingQuote,
  successDisplay
}) => {
  const [t] = useTranslation();
  const userType = useSelector(s => s.common.userType);
  const isGuest = userType === USER_TYPE_GUEST;
  const shoppingPreferencesImageSettings = useSelector(
    s => s.shoppingPreferences?.shoppingPreferencesData?.imageSettings
  );
  const isCatCSR = useSelector(s => s.common.isCatCSR) || false;
  const searchParams = useLocation().search;
  const { orderType } = queryString.parse(searchParams);
  const isCheckFreightSuccessful = useSelector(
    s => s?.checkout?.isCheckFreightCallSuccessful
  );

  const taxDisclaimerMessageFallback = useSelector(
    s => s.orders.taxDisclaimerMessage
  );

  const taxMessage = taxDisclaimerMessage ?? taxDisclaimerMessageFallback;

  const csrImageSettings = [
    {
      default: true,
      displayName: 'Display on screen',
      value: 'displayOnScreen'
    },
    {
      default: true,
      displayName: 'Display on print',
      value: 'displayOnPrint'
    }
  ];

  const imageSettings = isCatCSR
    ? csrImageSettings
    : shoppingPreferencesImageSettings;

  const schema = [
    {
      className: TABLE_CELL_STYLES_DESKTOP,
      id: 'index'
    },
    {
      className: TABLE_CELL_STYLES_DESKTOP,
      columnType: columnTypes.FIXED,
      dataType: dataTypes.ICON,
      id: 'quantity',
      label: t('QUANTITY1')
    },
    {
      columnType: columnTypes.ICON,
      id: 'image',
      label: null
    },
    {
      className: TABLE_CELL_STYLES_DESKTOP,
      columnType: columnTypes.STRETCHED,
      id: 'name',
      label: t('CAT_COLLAPS_PRODUCT_DISCRIPTION')
    },
    {
      className: TABLE_CELL_STYLES_DESKTOP,
      columnType: columnTypes.STRETCHED,
      id: 'lineItemNotes',
      label: t('NOTES')
    },
    showAvailabilityColumn && {
      className: TABLE_CELL_STYLES_DESKTOP,
      columnType: columnTypes.STRETCHED,
      id: 'availability',
      label: (
        <ItemOrderTableAvalabilityLabel
          isQuantityMergeEnabled={isQuantityMergeEnabled}
        />
      )
    },
    showUnitPriceColumn && {
      className: TABLE_CELL_STYLES_DESKTOP,
      columnType: columnTypes.STRETCHED,
      dataType: dataTypes.NUMBER,
      label: (
        <ItemsInOrderTablePriceLabel
          currencyNotation={currencyNotation}
          includeGST={includeGST}
          taxDisclaimerMessage={taxMessage}
          isCheckFreightSuccessful={isCheckFreightSuccessful}
          isQuantityMergeEnabled={isQuantityMergeEnabled}
          orderType={ORDER_TYPES.OFFLINE_ORDER}
        />
      ),
      id: 'unitPrice'
    },
    {
      className: TABLE_CELL_STYLES_DESKTOP,
      columnType: columnTypes.STRETCHED,
      dataType: dataTypes.NUMBER,
      label: (
        <ItemsInOrderTablePriceLabel
          currencyNotation={currencyNotation}
          includeGST={includeGST}
          taxDisclaimerMessage={taxMessage}
          isCheckFreightSuccessful={isCheckFreightSuccessful}
          isQuantityMergeEnabled={isQuantityMergeEnabled}
          orderType={orderType}
          showGST={showGST}
          showGstDagger={showGstDagger}
        />
      ),
      id: 'totalPrice'
    },
    {
      className: TABLE_CELL_STYLES_MOBILE,
      id: 'condensedRow'
    }
  ].filter(schema => schema);

  const data = items.map((item, index) => {
    return {
      availability: item.availability,
      sourcingDetails: item.sourcingDetails,
      coreDeposit: item.coreDeposit,
      imageURL: item.imageURL,
      productDisplayUrl: item.productDisplayUrl,
      lineItemNotes: item?.orderItemExtendAttribute?.lineItemNotes,
      name: item.name,
      nonReturnablePartNumber:
        item?.orderItemExtendAttribute?.nonReturnablePartNumber,
      quantity: item.quantity,
      boQty: item.boQty,
      reactKey: item.orderItemId,
      remanPartNumber: item?.orderItemExtendAttribute?.remanPartNumber,
      ssroBundle: item?.orderItemExtendAttribute?.ssroBundle,
      prop65Warning: item?.orderItemExtendAttribute?.prop65Warning,
      strikeThrough: item?.strikeThrough,
      totalPrice: item.totalPrice,
      totalPriceWithGST: item?.totalPriceWithGST,
      undiscountedPrice: item?.undiscountedPrice,
      unitPrice: item.unitPrice,
      unitPriceWithGST: item?.unitPriceWithGST,
      discount: item.discount,
      extraCharge: item.extraCharge,
      unitWeight: item?.orderItemExtendAttribute?.unitWeight,
      // We previously map hoseAssemblyDetails in the item object which is only used in order confirmation
      hoseAssemblyDetails: item?.hoseAssemblyDetails || {
        hoseAssemblyIndicator:
          item?.orderItemExtendAttribute?.hoseAssemblyIndicator,
        hoseAvailabilityLabel:
          item?.orderItemExtendAttribute?.hoseAvailabilityLabel
      },
      itemGADetails: item.itemGADetails,
      storeAvailability: item.storeAvailability,
      refreshSourcingDetails:
        orderItemPricesDetails &&
        orderItemPricesDetails[index]?.sourcingDetails,
      refreshAvailability:
        orderItemPricesDetails && orderItemPricesDetails[index]?.availability,
      refreshStoreAvailability:
        orderItemPricesDetails &&
        orderItemPricesDetails[index]?.storeAvailability
    };
  });

  const isLG = useBreakpoint(PAGE_BREAKPOINTS.LG);
  const imageSize = isLG ? '96px' : '72px';
  const getIndexCell = (item, col, thirdParam, rowIndex) => `${rowIndex + 1})`;
  const getNameCell = item => (
    <ItemsInOrderTablePartDescription {...item} listName={listName} />
  );

  const getImageCell = item =>
    item.imageURL && (
      <ItemsInOrderTableImage
        height={imageSize}
        width={imageSize}
        name={item?.name}
        url={item?.imageURL}
      />
    );
  const getPriceCell = item => (
    <ItemsInOrderTablePrice
      currencyNotation={currencyNotation}
      includeGST={includeGST}
      showGST={showGST}
      isCheckFreightSuccessful={isCheckFreightSuccessful}
      orderType={orderType}
      showUnitPriceColumn={showUnitPriceColumn}
      {...item}
    />
  );

  const getUniPriceCell = item => <ItemsInOrderTableUnitPrice {...item} />;

  const getAvailabilityCell = item => {
    return orderType === ORDER_TYPES.OFFLINE_ORDER ? (
      <div className="text-center">{item?.boQty}</div>
    ) : (
      <Availability
        availabilityInformation={
          successDisplay && isPendingQuote
            ? item?.refreshAvailability
            : item.availability
        }
        sourcingDetailsInformation={
          successDisplay && isPendingQuote
            ? item?.refreshSourcingDetails
            : item.sourcingDetails
        }
        availabilityTypeShowToggle={displayAvailabityAndSourcingDetails}
        hideLabel
        hoseAssemblyDetails={item.hoseAssemblyDetails}
        storeAvailability={
          successDisplay && isPendingQuote
            ? item?.refreshStoreAvailability
            : item.storeAvailability
        }
      />
    );
  };

  const getLineItemNoteCell = item => {
    const lineItemNotes = item?.lineItemNotes || {};
    return <ItemsInOrderTableNotes {...lineItemNotes} />;
  };
  const getCondensedRow = item => (
    <div className="d-print-none">
      <ItemsInOrderTableCondensedRow
        currencyNotation={currencyNotation}
        imageSize={imageSize}
        productDisplayUrl={item.productDisplayUrl}
        includeGST={includeGST}
        taxDisclaimerMessage={taxMessage}
        shoppingPreferencesImageSettings={imageSettings}
        showGST={showGST}
        isCheckFreightSuccessful={isCheckFreightSuccessful}
        showUnitPriceColumn={showUnitPriceColumn}
        displayAvailabityAndSourcingDetails={
          displayAvailabityAndSourcingDetails
        }
        isGuest={isGuest}
        {...item}
        listName={listName}
        isQuantityMergeEnabled={isQuantityMergeEnabled}
        storeAvailability={item.storeAvailability}
      />
    </div>
  );
  const getCustomRenderers = () => {
    return {
      availability: getAvailabilityCell,
      index: getIndexCell,
      lineItemNotes: getLineItemNoteCell,
      image: getImageCell,
      name: getNameCell,
      unitPrice: getUniPriceCell,
      totalPrice: getPriceCell,
      condensedRow: getCondensedRow
    };
  };
  const squemaByShoppingPreferences = getUpdatedSchema(
    imageSettings,
    schema,
    isGuest
  );
  return (
    <div className={`${stylesModule['items-in-order-table__table-print']}`}>
      <Table
        className={`${styles['text-xs']} ${checkoutStyles['items-in-order-table']}`}
        schema={squemaByShoppingPreferences}
        data={data}
        customRenderers={getCustomRenderers()}
      />
    </div>
  );
};

ItemsInOrderTable.propTypes = {
  currencyNotation: PropTypes.string.isRequired,
  includeGST: PropTypes.bool,
  taxDisclaimerMessage: PropTypes.string,
  items: declarations.orderItems,
  showGST: PropTypes.bool,
  isQuantityMergeEnabled: PropTypes.bool,
  displayAvailabityAndSourcingDetails: PropTypes.bool,
  listName: PropTypes.string,
  showAvailabilityColumn: PropTypes.bool,
  showGstDagger: PropTypes.bool,
  showUnitPriceColumn: PropTypes.bool,
  isPendingQuote: PropTypes.bool,
  successDisplay: PropTypes.bool
};

export default ItemsInOrderTable;
